import { computed, ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useAppStore } from '@/dashboard/store/appStore';
import { useWorkspaceAddAndEdit } from '@/dashboard/store/workspaceAddEditStore';
import TeamUser from '@/dashboard/types/teamUser';
import { Step } from '@/dashboard/types/step';
import { OwnerAdminCreatorViewerRoles, OwnerAdminRoles } from '@/dashboard/constants/roles';
import { UserRole } from '@/dashboard/types/user_role';
import { WorkspaceApi } from '@/dashboard/api/workspace';
import { useAlertStore } from '@/dashboard/store/alertStore';

export function useWorkspaceTitlePanel() {
  const router = useRouter();
  const appStore = useAppStore();
  const alertStore = useAlertStore();
  const workspaceAddAndEdit = useWorkspaceAddAndEdit();

  const { workspace, currentWorkspaceUser, baseWorkspaceShortLink } = storeToRefs(appStore);

  const isOwner = computed(() => currentWorkspaceUser.value?.role === UserRole.Owner);
  const isOwnerOrAdmin = computed(() => {
    if (currentWorkspaceUser.value) {
      return OwnerAdminRoles.includes(currentWorkspaceUser.value.role);
    } else {
      return false;
    }
  });
  const canShowMenu: ComputedRef<boolean> = computed(() => {
    if (currentWorkspaceUser.value) {
      return OwnerAdminCreatorViewerRoles.includes(currentWorkspaceUser.value.role);
    } else {
      return false;
    }
  });
  const canAddUsers: ComputedRef<boolean> = computed(() => isOwnerOrAdmin.value);
  const canEdit: ComputedRef<boolean> = computed(() => isOwnerOrAdmin.value);
  const canLeave = computed(() => !isOwner.value);
  const canDelete = computed(() => {
    if (workspace.value) {
      return workspace.value.is_owner && !workspace.value.temporary;
    } else {
      return false;
    }
  });
  const team: ComputedRef<TeamUser[]> = computed(() => {
    return (workspace.value?.users ?? []).map((item) => new TeamUser(item.email, item.role, 'workspace', item.image, true));
  });
  const menuItems = computed(() => {
    return [
      {
        canRender: canEdit.value,
        action: openEditWorkspace,
        icon: 'SettingsIcon',
        label: 'Settings',
        labelClass: 'text-black dark:text-white',
        iconClass: 'stroke-black dark:stroke-white',
        help: null,
      },
      {
        canRender: canLeave.value,
        action: () => openConfirmAlert(),
        icon: 'LogoutIcon',
        label: 'Leave workspace',
        labelClass: 'text-black dark:text-white',
        iconClass: 'stroke-black dark:stroke-white',
        help: 'If you leave the workspace with captures in your personal directory, it will be permanently deleted along with all captures.',
      },
      {
        canRender: false,
        action: () => {},
        icon: 'ArchiveIcon',
        label: 'Archive directory',
        labelClass: 'text-black dark:text-white',
        iconClass: 'stroke-black dark:stroke-white',
        help: null,
      },
      {
        canRender: canDelete.value,
        action: deleteWorkspacePopup,
        icon: 'DeleteIcon',
        label: 'Delete workspace',
        labelClass: 'text-hard-error',
        iconClass: 'stroke-hard-error',
        help: 'When a workspace is deleted, all directories, captures and information will be permanently deleted.',
      },
    ].filter((item) => item.canRender);
  });

  function addUser() {
    if (workspace.value) {
      workspaceAddAndEdit.openPopup(false, workspace.value, Step.Team);
    }
  }

  function openEditWorkspace() {
    if (workspace.value) {
      workspaceAddAndEdit.openPopup(false, workspace.value);
    }
  }

  function deleteWorkspacePopup() {
    appStore.openDeleteWorkspacePopup();
  }

  async function leaveWorkspace() {
    if (workspace.value && currentWorkspaceUser.value) {
      const data = await WorkspaceApi.deleteUser({
        keepPersonalData: true,
        userId: currentWorkspaceUser.value.id,
        workspaceShortLink: workspace.value.short_link,
      });

      if (data.success) {
        await appStore.loadWorkspaces();
        await router.push({
          name: 'workspace',
          params: {
            short_link: baseWorkspaceShortLink.value,
          },
        });
      }
    }
  }

  function openConfirmAlert() {
    alertStore.open({
      title: 'Are you sure?',
      subtitle: `If you leave the workspace, your personal directory (in the workspace: ${workspace.value?.name}) will be permanently deleted.`,
      action: leaveWorkspace,
      cancelAction: () => {},
    });
  }

  return {
    canShowMenu,
    canAddUsers,
    canEdit,
    canDelete,
    team,
    menuItems,
    workspace,
    addUser,
    openEditWorkspace,
    deleteWorkspacePopup,
  };
}
